import { useState } from 'react';
import { useActivePlayer } from '@/context/ActivePlayerContext';
import Modal from '@/common/Modal';
import Tile from '@/common/Tile';
import EditableTile from '@/common/EditableTile';
import { TourBagDetail, TourBagIronDetail } from '@/api/models';
import { calculateFinalHeadWeight } from '../Iron/caluclateFinalHeadWeight';
import styles from './FinalHeadWeightModal.module.scss';

const sanitize = (value: string) => {
  return value.replace(/[^0-9.]/g, ''); // keep only digits + dot
};

export type IFinalHeadWeightIron = Pick<TourBagIronDetail, 'tourBagIronDetailId' | 'headWeight' | 'drill' | 'tipPlug' | 'glueWeight' | 'leadTape'>;

interface IFinalHeadWeightModalProps {
  showFinalHWModal: boolean;
  closeFinalHWModal: () => void;
  iron: TourBagDetail;
  // eslint-disable-next-line no-unused-vars
  handleUpdate?: (iron: IFinalHeadWeightIron) => void;
  isEditMode?: boolean;
}

function FinalHeadWeightModal({ showFinalHWModal, closeFinalHWModal, iron: club, handleUpdate, isEditMode = false }: IFinalHeadWeightModalProps) {
  const { player } = useActivePlayer();
  const [headWeight, setHeadWeight] = useState(club.headWeight || '');
  const [drill, setDrill] = useState(club.drill || '');
  const [tipPlug, setTipPlug] = useState(club.tipPlug || '');
  const [glueWeight, setGlueWeight] = useState(club.glueWeight || '');
  const [leadTape, setLeadTape] = useState(club.leadTape || '');
  const finalHeadWeight = calculateFinalHeadWeight({ headWeight, drill, tipPlug, glueWeight, leadTape });

  const onUpdate = async () => {
    try {
      const partialIron = {
        tourBagIronDetailId: club.tourBagDetailId,
        headWeight: Number(headWeight),
        drill: Number(drill),
        tipPlug: Number(tipPlug),
        glueWeight: Number(glueWeight),
        leadTape: Number(leadTape),
      };
      handleUpdate?.(partialIron);
      closeFinalHWModal();
    } catch (error) {
      console.error('Failed to Edit Iron final head weight: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    }
  };

  const onCancel = () => {
    setHeadWeight(club.headWeight || '');
    setDrill(club.drill || '');
    setTipPlug(club.tipPlug || '');
    setGlueWeight(club.glueWeight || '');
    setLeadTape(club.leadTape || '');
    closeFinalHWModal();
  };

  return (
    <Modal isOpen={showFinalHWModal} onClose={closeFinalHWModal} className={`${styles.modal} ${isEditMode ? styles.whiteBackground : styles.grayBackground}`}>
      <div className={styles.container}>
        <div className={styles.title}>Final Head Weight</div>
        <div className={styles.info}>
          {player?.fullName} - {club.familyName} {club.headModelName}
        </div>
        <div className={styles.tiles}>
          {!isEditMode && (
            <>
              <Tile label="HW" value={headWeight?.toString() || '---'} />
              <Tile label="Drill" value={drill?.toString() || '---'} />
              <Tile label="Tip Plug" value={tipPlug?.toString() || '---'} />
              <Tile label="Glue" value={glueWeight?.toString() || '---'} />
              <Tile label="Lead Tape" value={leadTape?.toString() || '---'} />
              <Tile label="Final HW" value={finalHeadWeight?.toString() === '0' ? '---' : finalHeadWeight?.toString()} />
            </>
          )}
          {isEditMode && (
            <div className={styles.editableTiles}>
              <EditableTile label="HW" value={headWeight?.toString() || '---'} onChange={(newValue) => setHeadWeight(sanitize(newValue))} />
              <EditableTile label="Drill" value={drill?.toString() || '---'} onChange={(newValue) => setDrill(sanitize(newValue))} />
              <EditableTile label="Tip Plug" value={tipPlug?.toString() || '---'} onChange={(newValue) => setTipPlug(sanitize(newValue))} />
              <EditableTile label="Glue" value={glueWeight?.toString() || '---'} onChange={(newValue) => setGlueWeight(sanitize(newValue))} />
              <EditableTile label="Lead Tape" value={leadTape?.toString() || '---'} onChange={(newValue) => setLeadTape(sanitize(newValue))} />
              <Tile label="Final HW" value={finalHeadWeight?.toString() === '0' ? '---' : finalHeadWeight?.toString()} />
            </div>
          )}
        </div>
        <div className={styles.buttons}>
          {isEditMode && (
            <>
              <button className={styles.update} onClick={onUpdate}>
                Update
              </button>
              <button className={styles.close} onClick={onCancel}>
                Cancel
              </button>
            </>
          )}
          {!isEditMode && (
            <>
              <button className={styles.close} onClick={closeFinalHWModal}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default FinalHeadWeightModal;
